import * as React from 'react';
import { Dispatch } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { formatRange } from 'shared/src/date-string-utils/date-utils';
import { Button } from '@/components/forms/ui/button';
import { formatCurrency } from '@/components/tables/table-utils';
import { cn } from '@/lib/utils';
import { MoneyInput } from './money-input';
import { DateRangeField } from './date-range-field';
import {
  blockUnits,
  dailyUnits,
  genColor
} from '@/features/line-items/line-item-form/utils/pacing-display';
import { PacingBlockClient } from '@/features/line-items/line-item-form/common/form-state';
import { PacingConfig } from '@/features/line-items/line-item-form/ui/pacing';
import { CommonAction } from '@/features/line-items/line-item-form/common/form-reducer';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';
import { LineItemType } from 'shared/src/domain/line-item/line-item-types';

type Props = {
  config: PacingConfig;
  dispatch: Dispatch<CommonAction>;
  errorFor?: (field: string) => string | undefined;
  warningFor?: (field: string) => string | undefined;
};

export function PacingBlocksGrid({ config, dispatch, errorFor, warningFor }: Props) {
  const unitLabel = config.unit_label;

  function onUpdateBlock(index: number, patch: Partial<PacingBlockV2>) {
    dispatch({ type: 'PACING_UPDATE_BLOCK', index, patch });
  }

  function onDeleteBlock(index: number) {
    dispatch({ type: 'PACING_REMOVE_BLOCK', index });
  }

  return (
    <div className="grid grid-cols-[auto_8rem_16rem_auto_auto_auto_1fr_auto] items-center gap-2">
      {config.rows.map(({ index, block, dailyBudget, spent }) => {
        const isLifetime = config.pacing_type === 'lifetime';
        const dateError = errorFor?.(`pacing_blocks.${index}.dates`);
        const budgetError = errorFor?.(`pacing_blocks.${index}.budget`);
        const budgetWarning = warningFor?.(`pacing_blocks.${index}.budget`);
        const hasBillableRevenue = spent !== undefined && spent > 0;

        const label = isLifetime ? 'Lifetime Block' : `Block ${index + 1}`;
        const budgetCell = isLifetime ? (
          <ReadOnlyCell value={block.budget === undefined ? '' : formatCurrency(block.budget)} />
        ) : (
          <BudgetInput
            index={index}
            budget={block.budget}
            onUpdateBlock={onUpdateBlock}
            error={budgetError}
            warning={budgetWarning}
          />
        );
        const dateCell = isLifetime ? (
          <ReadOnlyCell
            value={block.dates.from && block.dates.to ? formatRange(block.dates) : ''}
          />
        ) : (
          <DateEditor
            index={index}
            value={block.dates}
            minDate={config.dates.from}
            maxDate={config.dates.to}
            onChange={next => onUpdateBlock(index, { dates: next })}
            error={dateError}
          />
        );
        const computedCells = (
          <>
            <ReadOnlyCell
              value={dailyBudget === undefined ? '' : formatCurrency(dailyBudget)}
              label="Daily $"
            />
            <ReadOnlyCell value={blockUnits(block.budget, config.unit_cost)} label={unitLabel} />
            <ReadOnlyCell
              value={dailyUnits(block, config.unit_cost)}
              label={`Daily ${unitLabel}`}
            />
          </>
        );
        const deleteButton = isLifetime ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Delete pacing block ${index + 1}`}
            onClick={() => onDeleteBlock(index)}>
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        );

        if (hasBillableRevenue) {
          return (
            <div
              key={index}
              className="col-span-full flex items-stretch overflow-hidden rounded-md bg-slate-50">
              <div className={cn(genColor(index), 'w-1')} />
              <div className="flex flex-1 flex-col gap-2 p-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center pl-1 text-sm">{label}</div>
                  <div className="w-32 shrink-0">{budgetCell}</div>
                  <div className="w-64 shrink-0">{dateCell}</div>
                  {computedCells}
                </div>
                <RevenuePill total={spent} lineItemType={config.lineItemType} />
              </div>
              {deleteButton ? <div className="flex items-start p-1">{deleteButton}</div> : null}
            </div>
          );
        }

        return (
          <div key={index} className="contents">
            <BlockLabel index={index} text={label} />
            {budgetCell}
            {dateCell}
            {computedCells}
            <div />
            {deleteButton ?? <div />}
          </div>
        );
      })}
    </div>
  );
}

function BudgetInput({
  index,
  budget,
  onUpdateBlock,
  error,
  warning
}: {
  index: number;
  budget: number | undefined;
  onUpdateBlock: (index: number, patch: Partial<Omit<PacingBlockClient, 'origin'>>) => void;
  error?: string | undefined;
  warning?: string | undefined;
}) {
  const [value, setValue] = React.useState<number | undefined>(budget);
  React.useEffect(() => {
    setValue(budget);
  }, [budget]);

  function handleBlur() {
    if (value === undefined) return;
    onUpdateBlock(index, { budget: value });
  }

  const showWarning = !error && warning !== undefined;
  return (
    <div className="flex flex-col" data-error-id={`pacing_blocks.${index}.budget`}>
      <div
        className={cn(
          error && 'rounded-md ring-1 ring-red-500',
          showWarning && 'rounded-md ring-1 ring-amber-500'
        )}>
        <MoneyInput
          id={`block-${index}-budget`}
          aria-label={`Block ${index + 1} budget`}
          value={value}
          onChange={setValue}
          onBlur={handleBlur}
        />
      </div>
      {error ? (
        <span className="mt-1 text-xs text-red-600">{error}</span>
      ) : showWarning ? (
        <span className="mt-1 text-xs text-amber-700">{warning}</span>
      ) : null}
    </div>
  );
}

function DateEditor({
  index,
  value,
  minDate,
  maxDate,
  onChange,
  error
}: {
  index: number;
  value: ISODateRange;
  minDate: string;
  maxDate: string;
  onChange: (next: ISODateRange) => void;
  error: string | undefined;
}) {
  return (
    <div className="flex flex-col" data-error-id={`pacing_blocks.${index}.dates`}>
      <div className={cn(error && 'rounded-md ring-1 ring-red-500')}>
        <DateRangeField value={value} minDate={minDate} maxDate={maxDate} onChange={onChange} />
      </div>
      {error ? <span className="mt-1 text-xs text-red-600">{error}</span> : null}
    </div>
  );
}

function ColorBar({ index }: { index: number }) {
  return <div className={cn(genColor(index), 'w-1 rounded-2xl mr-2')} />;
}

function BlockLabel({ index, text }: { index: number; text: string }) {
  return (
    <div className="mr-2 flex items-center pl-2 text-sm">
      <ColorBar index={index} />
      <div>{text}</div>
    </div>
  );
}

function ReadOnlyCell({ value, label }: { value: string; label?: string }) {
  if (!value) return <div />;
  return (
    <div className="flex rounded-md bg-gray-100 px-2 py-1 text-sm">
      <div className={label ? 'mr-2' : ''}>{value}</div>
      {label ? <div className="font-light text-gray-400">{label}</div> : null}
    </div>
  );
}

function RevenuePill({ total, lineItemType }: { total: number; lineItemType: LineItemType }) {
  if (total <= 0) return <div />;
  return (
    <div className="flex items-center text-sm">
      <div className="rounded-l-sm bg-lime-300 px-2 py-1 font-light">$</div>
      <div className="rounded-r-sm bg-lime-100 px-2 py-1">
        <span className="mr-1 font-medium">${Math.round(total).toLocaleString('en-US')}</span>
        <span>{lineItemType === 'standard' ? 'billable revenue to date' : 'spend to date'}</span>
      </div>
    </div>
  );
}
